/**
 * The three places an API problem is shown (COMPONENTS.md "Error and permission mapping"):
 *   - ProblemBlock: a page or list that could not load — a StateBlock chosen by presentProblem;
 *   - ErrorBanner:  a refresh failed but older data is still on screen — banner above it, data kept;
 *   - notifyProblem: an action failed — a toast with the message, "Try again" only if retryable.
 */
import { TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../primitives/button';
import { cn } from '../utils';
import { presentProblem, toApiProblem, type ApiProblem } from './api-problem';
import { StateBlock } from './StateBlock';

export interface ProblemBlockProps {
  /** The thrown error, or an already-normalised problem. */
  readonly error: unknown;
  readonly onRetry?: (() => void) | undefined;
  /** Shown on the subscription block ("Go to billing"). */
  readonly onBilling?: (() => void) | undefined;
  readonly className?: string | undefined;
}

function asProblem(error: unknown): ApiProblem {
  return error && typeof error === 'object' && 'retryable' in error && 'code' in error && 'status' in error
    ? error as ApiProblem
    : toApiProblem(error);
}

export function ProblemBlock({ error, onRetry, onBilling, className }: ProblemBlockProps) {
  const problem = asProblem(error);
  const shown = presentProblem(problem);
  if (shown.as === 'fields') {
    // A page load does not have fields to attach errors to; show the summary.
    return <StateBlock kind="error" message={shown.message} requestId={problem.requestId} className={className} />;
  }
  return (
    <StateBlock
      kind={shown.kind}
      title={shown.title}
      message={shown.kind === 'error' ? problem.message : shown.message}
      retryable={problem.retryable}
      onRetry={onRetry}
      requestId={problem.requestId}
      docsUrl={problem.docsUrl}
      primary={shown.kind === 'subscription' && onBilling ? { label: 'Go to billing', onClick: onBilling } : undefined}
      className={className}
    />
  );
}

export interface ErrorBannerProps {
  readonly error: unknown;
  readonly onRetry?: (() => void) | undefined;
  readonly className?: string | undefined;
}

/** Inline, list-level: "Couldn’t refresh: <message>". The existing data stays underneath. */
export function ErrorBanner({ error, onRetry, className }: ErrorBannerProps) {
  const problem = asProblem(error);
  return (
    <div className={cn('ifx-error-banner', className)} role="alert">
      <TriangleAlert className="ifx-error-banner__icon" aria-hidden="true" />
      <span className="ifx-error-banner__text">Couldn’t refresh: {problem.message}</span>
      {problem.retryable && onRetry ? (
        <Button size="sm" variant="outline" onClick={onRetry}>Try again</Button>
      ) : problem.docsUrl ? (
        <Button asChild size="sm" variant="outline"><a href={problem.docsUrl} target="_blank" rel="noreferrer">What this means</a></Button>
      ) : null}
    </div>
  );
}

/**
 * A failed action: toast the server's message. "Try again" appears only when the server said a
 * retry could work, and re-runs `retry` (which should reuse the action's Idempotency-Key).
 * Returns the normalised problem so the caller can also mark form fields.
 */
export function notifyProblem(error: unknown, retry?: () => void): ApiProblem {
  const problem = asProblem(error);
  toast.error(problem.message, {
    ...(problem.requestId ? { description: `Reference ${problem.requestId}` } : {}),
    ...(problem.retryable && retry ? { action: { label: 'Try again', onClick: retry } } : {}),
  });
  return problem;
}
